import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import { getGallery } from '../../seo/galleryConfig';

const faqs = [
  {
    q: 'What is "property maintenance" — how is it different from one-off repairs?',
    a: 'Property maintenance is the proactive, ongoing care of a building: regular inspections, scheduled servicing of key systems (boilers, heating, drains), seasonal preparation, and quick attention to small issues before they become expensive problems. One-off repairs deal with the symptom. Property maintenance prevents the symptom from happening in the first place.'
  },
  {
    q: 'Do you offer maintenance contracts for landlords and agents?',
    a: 'Yes. We offer flexible monthly retainer arrangements for landlords with multiple properties, lettings agencies, and small commercial sites. The retainer covers a defined number of visit hours per month and includes priority response on emergencies. Pricing is bespoke based on portfolio size and travel coverage.'
  },
  {
    q: 'Can you do an annual property inspection?',
    a: 'Absolutely. An annual maintenance inspection is one of our most popular services — we walk the property room by room, test taps and toilets, check the heating system, inspect external brickwork and gutters, identify silicone and sealant deterioration, and write up a report with photos, recommendations and a prioritised cost plan.'
  },
  {
    q: 'Will you handle minor repairs there and then, or just report them?',
    a: 'Both. During a maintenance visit, anything that can be fixed in under 30 minutes with on-board tools and parts is usually sorted on the spot. Larger items are quoted and booked in. You decide what to action.'
  },
  {
    q: 'Do you provide written maintenance reports?',
    a: 'Every maintenance visit ends with a written report, including photographs, sent to you by email within 48 hours. The report is suitable for landlord records, insurance documentation, and sale or letting due diligence.'
  },
  {
    q: 'Which areas do you cover for property maintenance?',
    a: 'We cover Greater London, Kent, and Essex on standard maintenance contracts. For larger portfolios or specialist sites we will gladly travel further across the South-East — get in touch for a coverage discussion.'
  },
];

const relatedServices = [
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'Standalone plumbing repairs and installs.' },
  { slug: 'handyman', label: 'Handyman Services', blurb: 'General multi-trade jobs covered in a single visit.' },
  { slug: 'emergency-plumber', label: 'Emergency Plumber', blurb: 'Out-of-hours response for genuine emergencies.' },
];

const PropertyMaintenancePage = () => (
  <ServicePageLayout
    seoTitle="Property Maintenance Services in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Professional property maintenance services for homeowners, landlords and lettings agents across Kent, London and Essex. Inspections, repairs, planned maintenance plans. Call +44 330 122 1094."
    canonicalPath="/services/property-maintenance"
    keywords="property maintenance, building maintenance, home repairs, landlord maintenance, lettings maintenance, property maintenance Kent, property maintenance London, property maintenance Essex"
    pageTitle="Property Maintenance for Homeowners, Landlords & Agents"
    serviceName="Property Maintenance"
    serviceSlug="property-maintenance"
    galleryImages={getGallery('property-maintenance')}
    heroSubheading="Prevent the expensive problems. Catch the small ones early."
    intro="A well-maintained property holds its value, lets faster, and avoids the kind of catastrophic failures that cost thousands to put right. H.A.R.P Solutions offers structured property maintenance services across Kent, Greater London and Essex — from annual inspections for single homeowners to retained maintenance contracts for landlords and lettings agents with growing portfolios."
    bullets={[
      'Annual or quarterly inspections with written reports',
      'Retained contracts for landlords and agents',
      'Multi-trade engineers — fewer subcontractors',
      'Photographed reports for insurance and tenancy records',
      'Priority response on emergencies for contract customers',
      'Transparent monthly billing — no surprises',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
  >
    <h2>Why Property Maintenance Matters</h2>
    <p>
      The single biggest cause of expensive property emergencies is small problems that nobody bothered to look at. A perished radiator
      valve becomes a flood. A slow toilet flush turns into a comprehensive cistern failure during a tenant's first week. A dripping
      external tap freezes in winter and cracks the pipe inside the wall. None of these failures are difficult to spot — but they only get
      spotted if someone is looking.
    </p>
    <p>
      Property maintenance is the discipline of looking. It is a small, predictable monthly cost that prevents the unpredictable, larger
      ones. Our property maintenance customers consistently see lower emergency bills, faster lettings turnaround, and far fewer awkward
      conversations with insurers.
    </p>

    <h2>Who We Work With</h2>
    <h3>Homeowners</h3>
    <p>
      For private homeowners we provide annual maintenance inspections with a written report covering plumbing, heating, internal joinery,
      sealants, and obvious external issues such as guttering and brickwork. The report is invaluable when you come to sell, refinance, or
      decide on improvement priorities. Many of our homeowner customers book us in for an annual "spring tune-up" — bleed radiators,
      reseal kitchens and bathrooms, service taps, check stopcocks, and tidy up any niggles.
    </p>

    <h3>Landlords</h3>
    <p>
      Landlords have a legal duty to maintain rental properties to a safe standard, and a commercial incentive to maximise lettings income.
      Our landlord maintenance service blends scheduled inspections, planned servicing of boilers and heating systems, mid-tenancy
      check-ins, and rapid response on emergencies. Reports are written specifically for landlord record-keeping and tenancy dispute
      evidence.
    </p>

    <h3>Lettings Agents</h3>
    <p>
      For lettings agencies managing 10 to 200+ properties, we offer retained-engineer contracts: a defined number of monthly visit hours,
      single point of contact, online job tracking, and one consolidated monthly invoice. This typically reduces both costs and
      administrative overhead compared with calling individual contractors for each issue.
    </p>

    <h3>Small Commercial &amp; Mixed-Use</h3>
    <p>
      Cafés, restaurants, gyms, salons, offices and small retail units all benefit from planned maintenance — taps that don't leak,
      toilets that always flush, heating that works on the coldest day of the year. We can attend out of hours so your trading is never
      disrupted.
    </p>

    <h2>What's Included In A Maintenance Visit</h2>
    <ul>
      <li>Walkthrough of every room — checking plumbing, heating outlets, fittings and visible joinery.</li>
      <li>Function-test of every tap, shower, toilet flush, isolation valve and stopcock.</li>
      <li>Visual inspection of boiler, hot-water cylinder and any visible pipework — flagging signs of corrosion or seepage.</li>
      <li>Check of radiator balance, valve condition, and bleeding where required.</li>
      <li>Inspection of internal sealants — bath, basin, shower, kitchen worktops — and resealing where deteriorated.</li>
      <li>Test of door latches, locks, handles, hinges; minor adjustments performed on the spot.</li>
      <li>External walk-around for visible gutter, brickwork, and outdoor tap issues.</li>
      <li>Written report with photographs, recommendations and prioritised quotes.</li>
    </ul>

    <h2>How Pricing Works</h2>
    <p>
      We offer three pricing structures depending on how you want to engage:
    </p>
    <ol>
      <li><strong>One-off inspection.</strong> A fixed fee for a single annual or pre-sale inspection, including the written report. Best for homeowners who want a periodic professional opinion without an ongoing commitment.</li>
      <li><strong>Pay-as-you-go retained.</strong> An hourly engineer rate at a discount versus our standard call rate, in exchange for committing to use us as your maintenance provider. No monthly fee. Ideal for landlords with one or two properties.</li>
      <li><strong>Monthly retainer.</strong> A fixed monthly fee covering a defined number of visit hours, priority response, and consolidated reporting. Best for lettings agents and landlords with multiple properties.</li>
    </ol>

    <h2>What Sets Our Maintenance Service Apart</h2>
    <h3>One engineer, many trades</h3>
    <p>
      Because our engineers are genuinely multi-skilled, a single visit can cover plumbing, joinery, sealants, minor electrical
      accessories, and general fix-ups. You are not paying three different contractors to come out for what should be one visit.
    </p>
    <h3>Real documentation</h3>
    <p>
      Every visit ends with a proper written report — not a tick-list. Photographs are dated and labelled. The report is easy to share with
      tenants, insurers, lawyers, or buyers.
    </p>
    <h3>One number, every time</h3>
    <p>
      Property maintenance customers always speak to the same team. We know your property, we know your previous repairs, and we don't
      waste your time re-explaining basics.
    </p>

    <h2>Areas Covered</h2>
    <p>
      Property maintenance contracts are available across <strong>Kent</strong>, <strong>Greater London</strong> and <strong>Essex</strong>.
      For portfolios concentrated in one borough or town we can usually offer enhanced response times. Talk to us about your coverage
      requirements.
    </p>

    <h2>Talk To Us About Your Property</h2>
    <p>
      Every property is different — and so is every owner's priority list. Call <a href="tel:+443301221094">
      <strong>+44 330 122 1094</strong></a>, message us via WhatsApp, or use the form below. We'll book in a free 15-minute discovery call
      to understand what you need and quote the most appropriate option. For one-off plumbing or handyman jobs see our{' '}
      <a href="/services/plumbing">plumbing</a> and <a href="/services/handyman">handyman</a> pages.
    </p>
  </ServicePageLayout>
);

export default PropertyMaintenancePage;
